import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { MessageCircle, Plus, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = { open: "#FBBF24", in_progress: "#00E5FF", resolved: "#34D399", closed: "#52525B" };

export default function Support() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [form, setForm] = useState({ subject: "", message: "", category: "general", priority: "normal" });

  const load = () => api.get("/tickets").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error("Subject and message required");
    await api.post("/tickets", form);
    setForm({ subject: "", message: "", category: "general", priority: "normal" });
    setOpen(false);
    toast.success("Ticket created — we'll get back to you.");
    load();
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    const { data } = await api.post(`/tickets/${active.ticket_id}/reply`, { message: reply });
    setActive(data); setReply("");
    load();
  };

  return (
    <div className="p-8 max-w-[1100px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline text-[#8A52FF]">[ SUPPORT ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight">Help center</h1>
          <p className="text-white/55 text-sm mt-1.5">Reach our team — we're here to help.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 rounded-full" data-testid="new-ticket-btn"><Plus className="h-4 w-4 mr-2" /> New ticket</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display">Open a new ticket</DialogTitle>
              <DialogDescription className="text-white/50">Tell us what's going on. We'll respond as soon as possible.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="kr-overline">Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-[#050505] border-white/10 mt-2" data-testid="ticket-subject-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="kr-overline">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-[#050505] border-white/10 mt-2" data-testid="ticket-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0E] border-white/10">
                      {["general", "billing", "bug", "feature", "account"].map((c) => <SelectItem key={c} value={c} className="focus:bg-white/5 capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="kr-overline">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="bg-[#050505] border-white/10 mt-2" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0E] border-white/10">
                      {["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p} className="focus:bg-white/5 capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="kr-overline">Message</Label>
                <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-[#050505] border-white/10 mt-2 resize-none" data-testid="ticket-message-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="ticket-submit-btn">Submit ticket</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 space-y-3">
        {items.map((t) => (
          <button key={t.ticket_id} onClick={() => setActive(t)} className="w-full text-left kr-card p-4 hover:bg-[#0c0c12]" data-testid={`ticket-row-${t.ticket_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display">{t.subject}</div>
                <div className="text-xs text-white/40 mt-0.5 line-clamp-1">{t.message}</div>
              </div>
              <div className="text-right shrink-0">
                <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border" style={{ borderColor: STATUS_COLORS[t.status] + "55", color: STATUS_COLORS[t.status] }}>{t.status.replace("_", " ")}</span>
                <div className="kr-overline mt-1.5">{new Date(t.created_at).toLocaleDateString()}</div>
              </div>
            </div>
          </button>
        ))}
        {items.length === 0 && (
          <div className="kr-card p-12 text-center text-white/40">
            <MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <div className="text-sm">No tickets yet. Start a conversation with our team.</div>
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{active.subject}</DialogTitle>
                <DialogDescription className="text-white/50">
                  Status: <span className="capitalize">{active.status.replace("_", " ")}</span> · Priority: <span className="capitalize">{active.priority}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="border-l-2 border-[#00E5FF]/40 pl-4">
                <div className="kr-overline">YOUR MESSAGE</div>
                <div className="text-sm mt-2 whitespace-pre-wrap">{active.message}</div>
              </div>
              <div className="mt-4 space-y-3">
                {(active.replies || []).map((r) => (
                  <div key={r.at + r.author} className={`p-3 border ${r.author === "admin" ? "border-[#8A52FF]/30 bg-[#8A52FF]/5" : "border-white/10 bg-white/[0.02]"}`}>
                    <div className="kr-overline" style={{ color: r.author === "admin" ? "#8A52FF" : "#00E5FF" }}>
                      {r.author === "admin" ? "ALPHACORE SUPPORT" : "YOU"}
                    </div>
                    <div className="text-sm mt-2 whitespace-pre-wrap">{r.message}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Add a reply…" className="bg-[#050505] border-white/10 resize-none" data-testid="user-ticket-reply-input" />
                <Button onClick={sendReply} disabled={!reply.trim()} className="mt-2 bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 w-full" data-testid="user-ticket-reply-send"><Send className="h-4 w-4 mr-2" /> Send</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
